
import {
  IonContent,
  IonPage,
  onIonViewWillEnter,
  IonButton,
  IonButtons,
  IonImg,
} from "@ionic/vue";
import { useStore } from "@/composables/useTypedStore";
import Toolbar from "@/components/Navigation/Toolbar";
import AButton from "@/components/Base/AButton";
import useAuth from "@/composables/useAuth";
import { computed, defineComponent, reactive, ref, toRef } from "vue";
import useUser from "@/composables/useUser";
import { getPhotoUrl } from "@/utilities/get-media-url";
import { popoverHelper } from "@/utilities/popover-helper";
import { getLocalMetaData } from "@/models/local-meta-data";
import { usePhotoRecording } from "@/composables/usePhotoRecording";
import UserDialogButton from "@/components/User/UserDialogButton.vue";
import SelectLanguage from "@/components/selectLanguage";
import AInput from "@/components/Base/AInput";
import { useI18n } from "vue-i18n";
import {
  email,
  helpers,
  minLength,
  required,
  sameAs,
} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import useAlert from "@/composables/useAlert";
import {logger} from "@/utilities/logging";
import {useRouter} from "vue-router";
import AFlatButton from "@/components/Base/AFlatButton.vue";
import useLogoutAlert from "@/composables/User/useLogoutAlert";
import useEnvVar from "@/composables/useEnvVar";

export default defineComponent({
  name: "Settings",
  components: {
    AFlatButton,
    AButton,
    Toolbar,
    IonPage,
    IonButton,
    IonButtons,
    IonContent,
    IonImg,
    AInput,
    UserDialogButton,
    SelectLanguage,
  },
  setup() {
    const store = useStore();
    const { user, updateProfileImage } = useUser();
    const { t } = useI18n({ useScope: "global" });
    const router = useRouter();
    const { getVar } = useEnvVar();

    onIonViewWillEnter(() => {
      store.commit("app/showBottomNavigation");
    });

    // @ts-ignore
    const isDev = getVar('NODE_ENV') == "development";

    const auth = useAuth();
    const alert = useAlert();

    const { openPhotoDialog } = usePhotoRecording();

    const backendBuildVersion = computed(() => {
      return getLocalMetaData("backendBuildVersion");
    });

    const uploadProfileImage = async () => {
      const photoRes = await openPhotoDialog(t, 1, {hideFile: true});
      photoRes.success && photoRes.data && photoRes.data.length > 0 && await updateProfileImage(photoRes.data[0]);
    };

    const changePasswordData = reactive({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
      userId: user.value?.id
    });

    const rules = {
      currentPassword: {
        required: helpers.withMessage(
          t("einstellungen.eingabeErforderlich"),
          required
        ),
      },
      newPassword: {
        required: helpers.withMessage(
          t("einstellungen.eingabeErforderlich"),
          required
        ),
        minLength: helpers.withMessage(
          t("helper.passwortLaenge"),
          minLength(8)
        ),
        goodPassword: helpers.withMessage(
          t("helper.passwortSicherheit"),
          (password: any) => {
            return (
              password.length >= 6 &&
              (/[a-z]/.test(password) || /[A-Z]/.test(password)) &&
              /[0-9]/.test(password)
            );
          }
        ),
      },
      confirmNewPassword: {
        required: helpers.withMessage(
          t("einstellungen.eingabeErforderlich"),
          required
        ),
        sameAsPassword: helpers.withMessage(
          t("einstellungen.passwortNichtIdent"),
          (confPassword: string) => {
            return (
              confPassword === changePasswordData.newPassword
            );
          }
        ),
      },
    };

    const changePasswordForm = useVuelidate(rules, {
      currentPassword: toRef(changePasswordData, "currentPassword"),
      newPassword: toRef(changePasswordData, "newPassword"),
      confirmNewPassword: toRef(changePasswordData, "confirmNewPassword"),
    });

    const onChangePassword = async () => {
      changePasswordForm.value.$touch();

      try {
        await auth.changePassword(changePasswordData);
        await alert.show(t("einstellungen.passwortGeändert"), "");
      } catch (error: any) {
        if (error.response?.data.message === "wrong-current-password") {
          await alert.show(t("einstellungen.falschesPasswort"), "");
        } else {
          await alert.show(t("alerts.unerwarteterFehlerHead"), t("alerts.unerwarteterFehlerText"));

          logger.error(`Resetting password failed: ${error}`);
        }
      }

      changePasswordData.currentPassword = "";
      changePasswordData.newPassword = "";
      changePasswordData.confirmNewPassword = "";
      changePasswordForm.value.$reset();
    };


    function goToLogs() {
      router.push('/logs')
    }

    return {
      logout: () => useLogoutAlert(router, t),
      getPhotoUrl,
      user,
      openPopover: popoverHelper,
      uploadProfileImage,
      backendBuildVersion,
      t,
      changePasswordForm,
      onChangePassword,
      goToLogs,
      rowClass: 'block md:flex mb-4',
      isDev,
      getVar,
    };
  },
});
